import React from "react";
import { Box } from "@material-ui/core";
import Highcharts from "highcharts";
import StockLine from "components/visuals/StockLine";
import useColourPalette from "components/visuals/useColourPalette";
import { dateFormat } from "highcharts";
import { useTheme } from "@material-ui/core/styles";

import { useAppSelector } from "store";
import { selectStore, selectComparator, selectMonthPeriodLabel, selectUseCustomCalendar, selectCustomCalendar } from "modules/customer/insights/portfolioNew/portfolioSlice";
import { selectGrossProfitMarginOverTime } from "modules/customer/insights/portfolioNew/profit/profitSlice";

import stringUtils from "utils/stringUtils";
import numberFormatter from "utils/numberFormatter";
import { abs } from "mathjs";

const GrossProfitMarginOverTime = () => {
    const theme = useTheme();
    const colourPalette = useColourPalette();
    const useCustomCalendar = useAppSelector(selectUseCustomCalendar);
    const customCalendar = useAppSelector(selectCustomCalendar);
    const monthPeriodLabel = useAppSelector(selectMonthPeriodLabel);
    const selectedStore = useAppSelector(selectStore);
    const selectedComparator = useAppSelector(selectComparator);
    const grossProfitMarginOverTime = useAppSelector(selectGrossProfitMarginOverTime);
    const { isLoading, hasErrors, data } = grossProfitMarginOverTime;

    const storeProfitMargin = data.store.map(function (current) {
        const storeDates = current.date.getTime();
        return {
            x: storeDates,
            y: current.profit,
            custom: {
                sales: current.sales,
                costs: current.costs
            }
        };
    });

    const comparatorProfitMargin = data.comparator.map(function (current) {
        const comparatorDates = current.date.getTime();
        return {
            x: comparatorDates,
            y: current.profit,
            custom: {
                sales: current.sales,
                costs: current.costs
            }
        };
    });

    const globalOptions = {
        lang: {
            rangeSelectorZoom: "Zoom"
        }
    };


    const getCustomLabel = (timestamp: string | number) => {
        if (typeof timestamp === "string" || customCalendar.length === 0) {
            return String(timestamp);
        }
        let difference = Number.MAX_VALUE;
        let dateIndex = 0;
        customCalendar.forEach((item, index) => {
            const newDifference = abs(timestamp - item.date.toMillis());
            if (newDifference < difference) {
                difference = newDifference;
                dateIndex = index;
            }
        });
        return customCalendar[dateIndex].periodLabel;
    };

    const options: Highcharts.Options = {
        title: {
            text: `${selectedStore?.name} vs ${selectedComparator?.name} gross profit margin % per ${monthPeriodLabel}`,
        },
        colors: colourPalette.comparators,
        series: [{
            name: selectedStore?.name,
            type: "spline",
            data: storeProfitMargin,
            xAxis: 0
        }, {
            name: selectedComparator?.name,
            type: "spline",
            data: comparatorProfitMargin,
            xAxis: 0
        }],
        legend: {
            enabled: true
        },
        rangeSelector: {
            enabled: false
        },
        scrollbar: {
            enabled: false
        },
        navigator: {
            enabled: false
        },
        tooltip: {
            useHTML: true,
            shared: true,
            split: false,
            // zIndex: 10,
            formatter: function () {
                if (!this.points) {
                    return "";
                }
                const selectedStorePoint = this.points[0];

                const header = useCustomCalendar ? getCustomLabel(selectedStorePoint.x as number) : dateFormat("%B %Y", selectedStorePoint.x as number);

                const valueArr: string[] = [];
                const categoryArr: string[] = [];
                const categoryFormatArr: string[] = [];
                this.points?.forEach(item => {
                    valueArr.push(
                        numberFormatter.toPercentage(item.point.y),
                        //@ts-ignore
                        numberFormatter.toGBP(item.point.custom.sales),
                        //@ts-ignore
                        numberFormatter.toGBP(item.point.custom.costs)
                    );
                    categoryArr.push(item.point.series.name, "Sales", "COGS");
                    categoryFormatArr.push(`color:${item.point.color};font-weight:bold`, "", "");
                });

                return `<table>${stringUtils.tooltipHTML(categoryArr, { header: header, values: valueArr, categoryFormattingArr: categoryFormatArr })}</table>`;

            }
        },
        xAxis: [{
            lineWidth: 0,
            type: "datetime",
            tickPositions: useCustomCalendar ? data.store.map(item => item.date.getTime()) : undefined,
            labels: useCustomCalendar ? {
                style: {
                    color: theme.palette.common.white,
                    fontSize: "12px"
                },
                formatter: function () {
                    return getCustomLabel(this.value);
                }
            } : {
                useHTML: true,
                style: {
                    color: theme.palette.common.white,
                    fontSize: "12px"
                },
                //@ts-ignore
                units: [["month", [1]]],
                dateTimeLabelFormats: {
                    month: "%b"
                }
            },
        }],
        yAxis: [{
            allowDecimals: true,
            opposite: false,
            gridLineColor: "#646174",
            plotLines: [{
                value: 0,
                color: "#FFFFFF",
                width: 2
            }],
            title: {
                text: "Gross profit margin",
                style: {
                    color: theme.palette.common.white
                }
            },
            labels: {
                style: {
                    color: theme.palette.common.white
                },
                format: '{text}%'
            }
        },
        ]
    };

    return (
        <Box data-cy="gross-profit-margin-over-time">
            <StockLine loading={isLoading} error={hasErrors} options={options} globalOptions={globalOptions} />
        </Box>
    );
};

export default GrossProfitMarginOverTime;
